import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { T } from "./Translate";
import { db, auth } from '../firebase';
import firebase from 'firebase/compat/app';
import {
  Alert,
  Box,
  Button,
  CardContent,
  CardMedia,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Link,
  OutlinedInput,
  Stack,
  Typography,
  Container
} from '@mui/material';
import { brandColors } from "../theme";
import logo13 from "../logo13.png";
const logo15 = `${process.env.PUBLIC_URL}/logo15.png`;

// Último recurso cuando ni el passkey ni el email de recuperación sirven
// (dispositivo perdido y casilla inaccesible). Esto NO restablece nada por sí
// solo: solo abre un ticket para revisión manual, como el "appeal" de
// Binance. La verificación real de identidad la hace una persona, por otro
// medio, antes de tocar la cuenta.
const AccountRecovery = () => {
  const [showAppealForm, setShowAppealForm] = useState(false);
  const [formData, setFormData] = useState({ email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (prop) => (event) => {
    setFormData({ ...formData, [prop]: event.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await db.collection('supportMessages').add({
        type: 'account-recovery',
        userId: auth.currentUser ? auth.currentUser.uid : null,
        email: formData.email.trim(),
        message: formData.message.trim(),
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        handled: false,
      });
      setSent(true);
    } catch (err) {
      setError('No se pudo enviar tu apelación. Intentá de nuevo en unos minutos.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="xs">
      <Box sx={{ mt: 8, pb: 4 }}>
        <CardMedia component="img" image={brandColors.mode === 'dark' ? logo13 : logo15} title="Notiline" sx={{ width: 180, mx: 'auto', mt: 4, mb: 2 }} />
        <CardContent>
          <Typography variant="h5" textAlign="center" fontWeight="300" gutterBottom>
            <T>Recuperar mi cuenta</T>
          </Typography>

          {!showAppealForm && (
            <>
              <Typography variant="body2" sx={{ color: brandColors.textSecondary, mb: 3, textAlign: 'center' }}>
                <T>Si todavía tenés acceso a tu email, esta es la forma más rápida y segura de volver a entrar.</T>
              </Typography>

              <Button component={RouterLink} to="/reset-password" fullWidth variant="contained" sx={{ height: 42 }}>
                <T>Restablecer contraseña por email</T>
              </Button>

              <Divider sx={{ my: 3 }}><Typography variant="caption" sx={{ color: brandColors.textSecondary }}><T>o</T></Typography></Divider>

              <Typography variant="body2" sx={{ color: brandColors.textSecondary, mb: 2, textAlign: 'center' }}>
                <T>¿Perdiste el dispositivo con tu llave de acceso y tampoco podés acceder a tu email? Apelá para que revisemos tu caso manualmente.</T>
              </Typography>

              <Button fullWidth variant="outlined" onClick={() => setShowAppealForm(true)} sx={{ height: 42 }}>
                <T>Apelar por mi cuenta</T>
              </Button>
            </>
          )}

          {showAppealForm && !sent && (
            <>
              <Typography variant="body2" sx={{ color: brandColors.textSecondary, mb: 2, textAlign: 'center' }}>
                <T>Contanos qué pasó. Un miembro del equipo va a revisar tu caso y puede pedirte más datos antes de restaurar el acceso — no es instantáneo.</T>
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <FormControl fullWidth margin="normal">
                  <InputLabel htmlFor="recovery-email"><T>Email de tu cuenta</T></InputLabel>
                  <OutlinedInput
                    id="recovery-email"
                    type="email"
                    label={<T>Email de tu cuenta</T>}
                    value={formData.email}
                    onChange={handleChange('email')}
                    placeholder="usuario@gmail.com"
                    disabled={isSubmitting}
                    autoComplete="email"
                    required
                  />
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <InputLabel htmlFor="recovery-message"><T>Contanos qué pasó</T></InputLabel>
                  <OutlinedInput
                    id="recovery-message"
                    multiline
                    minRows={4}
                    label={<T>Contanos qué pasó</T>}
                    value={formData.message}
                    onChange={handleChange('message')}
                    disabled={isSubmitting}
                    required
                  />
                </FormControl>

                <Button type="submit" fullWidth variant="contained" disabled={isSubmitting} sx={{ mt: 3, height: 42 }}>
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : <T>Enviar apelación</T>}
                </Button>
              </Box>
            </>
          )}

          {sent && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <T>Recibimos tu apelación. Te vamos a contactar al email que dejaste en cuanto revisemos tu caso.</T>
            </Alert>
          )}

          <Stack spacing={1} alignItems="center" sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
              <Link component={RouterLink} to="/login" sx={{ color: brandColors.accent }}><T>Volver al inicio de sesión</T></Link>
            </Typography>
          </Stack>
        </CardContent>
      </Box>
    </Container>
  );
};

export default AccountRecovery;
